/**
 * AdultAsdCriterionPanel — Main content panel for a single DSM-5 ASD criterion
 * in the Adult Autism Semi-Structured Interview.
 *
 * Layout:
 *   - Criterion title is pinned above the work area so clinicians always know
 *     which symptom they are on after scrolling a long probing-question list
 *     (same feedback as ADHD SSI).
 *   - Three columns aligned at the top. Childhood and Adulthood panels are
 *     only as tall as their content (evidence selectors, domain chips, notes
 *     box at its default min-height) and grow naturally as notes are added.
 *   - Probing Questions is capped to the work-area container height (measured
 *     via ResizeObserver, not a fixed vh guess) and scrolls internally when its
 *     accordion is long. The work area itself only scrolls when the note
 *     columns outgrow the container — never because of probing-question length.
 *   - While scrolling tall notes, the probing panel stays sticky within the
 *     work area so clinicians can still drag questions into view.
 *
 *   Column 1 (reference): Probing questions. Renders in one of three modes:
 *              • Life-stage-agnostic flat (criterion.probingQuestions defined):
 *                A single flat list — no accordion sections.
 *              • Categorised accordions (criterion.categorisedProbingQuestions):
 *                One collapsible accordion per named category. The first
 *                category opens by default on every criterion page load.
 *              • Legacy life-stage-split (adulthoodProbingQuestions /
 *                childhoodProbingQuestions): Childhood / Adulthood accordions
 *                (Childhood open by default). Preserved for other organisations.
 *   Column 2: Notes box for Childhood, with an evidence dropdown in the header.
 *   Column 3: Notes box for Adulthood, with an evidence dropdown in the header.
 *
 * Key differences from the CYP version (CypAsdCriterionPanel):
 *   - Simpler reference column: flat question lists instead of merged categories
 *   - Field keys use `_childhood` / `_adulthood` instead of `_early` / `_current`
 *   - No category merging/normalisation algorithm needed
 *
 * Shared components reused without modification:
 *   - StructuredNotesEditor for note cards with drag-drop and quote highlighting
 *   - PROBING_Q_MIME / DOMAIN_MIME for the drag-and-drop MIME contract
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, XIcon, SearchIcon } from '@heroicons/react/solid';
import StructuredNotesEditor, { PROBING_Q_MIME, DOMAIN_MIME } from '../../12_observation/components/StructuredNotesEditor';
import ModularStepFooter, { ModularPhaseSkipLink } from '../../../../components/autismModular/ModularStepFooter';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import InfoTooltip from '../../../../components/InfoTooltip';
import { EVIDENCE_OPTIONS, SCORING_NOTES, FUNCTIONAL_IMPAIRMENT_DOMAINS, CHILDHOOD_DOMAINS } from '../config/adultAsdSSIConfig';
import { AUTISM_EVIDENCE_BUTTON_COLOURS } from '../../../../constants/autismEvidenceColors';
import FormDetailsPanel from '../../5_patientOverview/components/FormDetailsPanel';

/**
 * Collapsible section for a group of probing questions (adulthood or childhood).
 * Uses ResizeObserver for smooth animated height transitions.
 */
function CollapsibleQuestionSection({ title, questions, isExpanded, onToggle, askedQuestions, onToggleAsked }) {
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const ro = new ResizeObserver(() => {
      setMeasuredHeight(inner.scrollHeight);
    });
    ro.observe(inner);
    setMeasuredHeight(inner.scrollHeight);

    return () => ro.disconnect();
  }, [questions]);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-150 text-left"
      >
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : 'rotate-0'
          }`}
        />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300"
        style={{
          maxHeight: isExpanded ? `${measuredHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
          transitionTimingFunction: isExpanded
            ? 'cubic-bezier(0.4, 0, 0.2, 1)'
            : 'cubic-bezier(0.4, 0, 1, 1)',
        }}
      >
        <div ref={innerRef} className="ml-6 mt-1 mb-2">
          <div className="flex flex-col gap-1.5">
            {questions.map((q, i) => {
              const isAsked = askedQuestions?.has(q);
              return (
                <div
                  key={i}
                  draggable="true"
                  onDragStart={(e) => {
                    e.dataTransfer.setData(PROBING_Q_MIME, q);
                    e.dataTransfer.setData('text/plain', q);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => onToggleAsked?.(q)}
                  className={`
                    px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                    cursor-pointer select-none transition-all duration-200
                    ${isAsked
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-800 hover:bg-blue-50/60'}
                  `}
                  title={isAsked ? 'Click to unmark' : 'Click to mark as asked \u2014 or drag into notes'}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="text-sm leading-relaxed flex-shrink-0 text-gray-400">&bull;</span>
                    <span>&ldquo;{q}&rdquo;</span>
                  </div>
                  {/* "Marked as asked" label with CSS grid row animation */}
                  <div
                    className="grid transition-all duration-300 ease-in-out"
                    style={{
                      gridTemplateRows: isAsked ? '1fr' : '0fr',
                      opacity: isAsked ? 1 : 0,
                      marginTop: isAsked ? '0.25rem' : '0px',
                    }}
                  >
                    <div className="overflow-hidden">
                      <span className="text-[10px] font-semibold text-blue-500 pl-5 not-italic block pb-0.5">
                        Marked as asked
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Evidence colour mapping — Clear = red, No evidence = green (shared Autism
 * scale). Imported rather than duplicated so SSI buttons cannot drift from
 * the OBS criterion buttons / evidence tables.
 */
const EVIDENCE_COLOURS = AUTISM_EVIDENCE_BUTTON_COLOURS;

export default function AdultAsdCriterionPanel({
  criterion,
  criterionData,
  onFieldChange,
  onMoveNote,
  moveTargets = null,
  onNavigate,
  onBackToHistory,
  criteria,
  displayPreferences = {},
  readOnly = false,
  showDomains = true,
  seqFormData = null,
}) {
  const val = (fieldId) => criterionData?.[fieldId] ?? null;

  // SEQ popup state -- only used for criterion B4 when the patient's org has
  // assigned the Sensory Experiences Questionnaire.
  const [isSeqPanelOpen, setIsSeqPanelOpen] = useState(false);

  // Auto-scroll the probing-questions column while dragging near its edges.
  const scrollRef = useRef(null);
  useDragAutoScroll(scrollRef);

  /**
   * Live height of the work-area scroll container. We cap the Probing Questions
   * panel to this value so a long accordion scrolls inside that column only.
   * ResizeObserver keeps the cap accurate across window resizes, footer/header
   * changes, and preview-mode banner toggles — without hard-coded vh offsets.
   */
  const workAreaRef = useRef(null);
  const [workAreaHeight, setWorkAreaHeight] = useState(null);

  useEffect(() => {
    const el = workAreaRef.current;
    if (!el) return;

    const syncHeight = () => {
      const { paddingTop, paddingBottom } = getComputedStyle(el);
      const padY = parseFloat(paddingTop) + parseFloat(paddingBottom);
      setWorkAreaHeight(el.clientHeight - padY);
    };
    syncHeight();

    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * Moves a note card between the Childhood and Adulthood notes columns via
   * drag-and-drop. The editor supplies LOCAL field keys (e.g. "notes_childhood")
   * for the active criterion, so we translate them into the global
   * "criterionId:fieldKey" ids that the parent's onMoveNote handler expects —
   * the same handler the cross-criterion Move picker uses, so there is a single
   * code path for all note moves. Delegates to onMoveNote (one atomic write);
   * falls back to sequential onFieldChange writes only if no handler is given.
   */
  const handleMoveNote = useCallback((sourceFieldKey, noteId, targetFieldKey, insertAt) => {
    if (onMoveNote) {
      onMoveNote(`${criterion.id}:${sourceFieldKey}`, noteId, `${criterion.id}:${targetFieldKey}`, insertAt);
      return;
    }
    // Fallback: two sequential writes (legacy path)
    const result = moveNoteBetweenArrays(val(sourceFieldKey), val(targetFieldKey), noteId, insertAt);
    if (!result) return;
    onFieldChange(sourceFieldKey, result.source);
    onFieldChange(targetFieldKey, result.target);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterion.id, criterionData, onFieldChange, onMoveNote]);

  const availableCriteria = criteria.filter(c => c.available);
  const currentIdx = availableCriteria.findIndex(c => c.id === criterion.id);
  const prevCriterion = currentIdx > 0 ? availableCriteria[currentIdx - 1] : null;
  const nextCriterion = currentIdx < availableCriteria.length - 1
    ? availableCriteria[currentIdx + 1]
    : null;

  const tooltipParts = [];
  if (SCORING_NOTES[criterion.criterionGroup]) {
    tooltipParts.push(SCORING_NOTES[criterion.criterionGroup]);
  }
  if (criterion.description) {
    tooltipParts.push(criterion.description);
  }
  const tooltipContent = tooltipParts.join('\n\n');

  /**
   * Which probing-question accordion is open (at most one at a time). Keys vary
   * by question shape:
   *   • Legacy split: 'childhood' | 'adulthood'
   *   • Categorised: category title string
   *
   * Always open the first accordion for the active criterion on page load /
   * criterion change (not only A1). useState alone would keep the previous
   * criterion's Set when the clinician navigates A1 → A2, so we reset in an
   * effect keyed on criterion.id.
   */
  const getDefaultExpandedSections = useCallback((crit) => {
    if (crit.categorisedProbingQuestions?.length) {
      return new Set([crit.categorisedProbingQuestions[0].category]);
    }
    // Legacy Childhood / Adulthood split — Childhood is the "first" accordion.
    return new Set(['childhood']);
  }, []);

  const [expandedSections, setExpandedSections] = useState(() => getDefaultExpandedSections(criterion));

  useEffect(() => {
    setExpandedSections(getDefaultExpandedSections(criterion));
    // Reset when the clinician navigates to a different criterion page so each
    // symptom opens with its first probing-questions accordion expanded.
  }, [criterion.id]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally criterion.id only

  /**
   * Exclusive accordion: opening one section closes any other. Clicking the
   * section that is already open collapses it so clinicians can close all.
   */
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      if (prev.has(sectionId)) {
        return new Set();
      }
      return new Set([sectionId]);
    });
  };

  const evidenceMode = displayPreferences.evidenceMode || 'dropdown';
  const navMode = displayPreferences.navMode || 'bottom';

  /**
   * Tracks which probing questions have been marked as "asked".
   * Stored as an array in criterionData, converted to Set for O(1) lookup.
   */
  const askedQuestionsSet = useMemo(
    () => new Set(val('asked_questions') || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [criterionData]
  );

  const toggleAskedQuestion = useCallback((question) => {
    if (readOnly) return;
    const current = val('asked_questions') || [];
    const next = current.includes(question)
      ? current.filter(q => q !== question)
      : [...current, question];
    onFieldChange('asked_questions', next);
  }, [onFieldChange, criterionData, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * One-way mark — called when a probing question is dragged into a notes box.
   * Unlike toggleAskedQuestion this only adds; it never removes so that dropping
   * the same question a second time doesn't accidentally un-mark it.
   */
  const markQuestionAsAsked = useCallback((question) => {
    if (readOnly) return;
    const current = val('asked_questions') || [];
    if (current.includes(question)) return;
    onFieldChange('asked_questions', [...current, question]);
  }, [onFieldChange, criterionData, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showAskedPopup, setShowAskedPopup] = useState(false);
  const askedQuestionsArray = useMemo(() => val('asked_questions') || [], [criterionData]); // eslint-disable-line react-hooks/exhaustive-deps

  const childhoodEvidence = val('evidence_childhood') || '';
  const adulthoodEvidence = val('evidence_adulthood') || '';

  /**
   * Renders the evidence selector — either dropdown or button row depending
   * on user display preference. Both modes use semantic colour coding.
   */
  const renderEvidenceSelector = useCallback((currentValue, stageFieldId, ariaLabel) => {
    const colours = EVIDENCE_COLOURS[currentValue];

    if (evidenceMode === 'buttons') {
      return (
        <div className="flex flex-wrap gap-1.5">
          {EVIDENCE_OPTIONS.map(opt => {
            const isSelected = currentValue === opt;
            const optColours = EVIDENCE_COLOURS[opt];
            return (
              <button
                key={opt}
                type="button"
                onClick={readOnly ? undefined : () => onFieldChange(stageFieldId, isSelected ? null : opt)}
                disabled={readOnly}
                className={`
                  px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150
                  ${readOnly ? 'cursor-default' : ''}
                  ${isSelected
                    ? `${optColours.selectedBg} text-white ${optColours.selectedBorder} shadow-sm`
                    : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : `${optColours.hoverBorder} hover:${optColours.text}`}`}
                `}
                aria-label={`${opt} \u2014 ${ariaLabel}`}
                aria-pressed={isSelected}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }

    const dropdownClasses = colours
      ? `px-2.5 py-1 rounded-lg text-xs font-semibold border shadow-sm focus:outline-none focus:ring-2 min-w-[130px] transition-all duration-150 ${colours.bg} ${colours.text} ${colours.border} focus:${colours.ring}`
      : 'px-2.5 py-1 rounded-lg text-xs font-semibold border border-gray-400 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 min-w-[130px]';

    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Evidence:</span>
        <select
          value={currentValue}
          onChange={readOnly ? undefined : (e) => onFieldChange(stageFieldId, e.target.value || null)}
          disabled={readOnly}
          className={`${dropdownClasses} ${readOnly ? 'cursor-default' : ''}`}
          aria-label={ariaLabel}
        >
          <option value="">Select</option>
          {EVIDENCE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }, [evidenceMode, onFieldChange, readOnly]);

  return (
    <div className="flex flex-col h-full text-left">
      {/* Pinned criterion header — stays visible while probing questions /
          notes columns scroll independently below. */}
      <div className="flex-shrink-0 px-6 pt-6 pb-3 border-b border-gray-200 bg-white z-10">
        {/* Top navigation — only shown when navMode is 'both' */}
        {navMode === 'both' && (
          <div className="flex justify-between items-start mb-4">
            <div className="flex flex-col items-start">
              {prevCriterion ? (
                <button
                  onClick={() => onNavigate(prevCriterion.id)}
                  className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" />
                  <span className="font-bold text-sm">{prevCriterion.id}</span>
                </button>
              ) : onBackToHistory ? (
                <button
                  onClick={onBackToHistory}
                  className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" />
                  <span className="font-bold text-sm">Back to History</span>
                </button>
              ) : (
                <div />
              )}
              {prevCriterion && onBackToHistory && (
                <ModularPhaseSkipLink
                  label="Back to History"
                  onClick={onBackToHistory}
                  direction="back"
                />
              )}
            </div>

            <div className="flex flex-col items-end">
              <button
                onClick={() => onNavigate(nextCriterion ? nextCriterion.id : 'REVIEW')}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-300"
              >
                <span className="font-bold text-sm">
                  {nextCriterion ? `Next: ${nextCriterion.id}` : 'Go to Review'}
                </span>
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
              {nextCriterion && (
                <ModularPhaseSkipLink
                  label="Go to Review"
                  onClick={() => onNavigate('REVIEW')}
                  direction="forward"
                />
              )}
            </div>
          </div>
        )}

        <div className="text-left">
          <div className="flex items-center gap-2">
            <h1 className="text-gray-900 text-3xl font-bold">
              {criterion.id}. {criterion.shortTitle}
            </h1>
            {tooltipContent && (
              <InfoTooltip
                content={tooltipContent}
                label={`${criterion.id} details`}
                maxWidth="380px"
              />
            )}
            {criterion.id === 'B4' && seqFormData && (
              <button
                type="button"
                onClick={() => setIsSeqPanelOpen(true)}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#43213E] text-[#43213E] hover:bg-[#F3E9F1] transition-colors duration-150"
              >
                <SearchIcon className="w-3.5 h-3.5" aria-hidden="true" />
                View Sensory Experiences Questionnaire
              </button>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1 font-semibold">
            {criterion.fullTitle.replace(/^[AB]\d+\.\s*/, '')}
          </p>
        </div>
      </div>

      {/* Work area scrolls only when Childhood / Adulthood notes exceed the
          available height. Probing Questions never grows the row beyond the
          work-area client height — it scrolls inside its own capped panel. */}
      <div ref={workAreaRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <div className="flex gap-4 items-start">

          {/* Column 1 — Probing questions: height capped to work area; internal
              scroll for long lists; sticky while clinician scrolls tall notes. */}
          <div
            ref={scrollRef}
            className="w-[320px] flex-shrink-0 self-start sticky top-0 overflow-y-auto bg-gray-50 rounded-2xl border border-gray-200 p-4"
            style={workAreaHeight != null ? { maxHeight: workAreaHeight } : undefined}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-slate-800 text-lg font-bold leading-tight text-left">
                Probing Questions
              </h2>
              {askedQuestionsArray.length > 0 && (
                <button
                  onClick={() => setShowAskedPopup(true)}
                  className="p-1 rounded-full text-gray-400
                    hover:text-primary hover:bg-primary/10
                    transition-colors duration-200"
                  title="View questions marked as asked"
                >
                  <SearchIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {criterion.categorisedProbingQuestions ? (
              // Categorised accordions — one collapsible section per named theme.
              // First category is open by default (reset on criterion change).
              <div className="flex flex-col gap-1">
                {criterion.categorisedProbingQuestions.map(({ category, questions }) => (
                  <CollapsibleQuestionSection
                    key={category}
                    title={category}
                    questions={questions}
                    isExpanded={expandedSections.has(category)}
                    onToggle={() => toggleSection(category)}
                    askedQuestions={askedQuestionsSet}
                    onToggleAsked={toggleAskedQuestion}
                  />
                ))}
              </div>
            ) : criterion.probingQuestions ? (
              // Life-stage-agnostic flat list — no accordion sections
              <div className="flex flex-col gap-1.5">
                {criterion.probingQuestions.map((q, i) => {
                  const isAsked = askedQuestionsSet.has(q);
                  return (
                    <div
                      key={i}
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.setData(PROBING_Q_MIME, q);
                        e.dataTransfer.setData('text/plain', q);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => toggleAskedQuestion(q)}
                      className={`
                        px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                        cursor-pointer select-none transition-all duration-200
                        ${isAsked
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-800 hover:bg-blue-50/60'}
                      `}
                      title={isAsked ? 'Click to unmark' : 'Click to mark as asked — or drag into notes'}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className="text-sm leading-relaxed flex-shrink-0 text-gray-400">&bull;</span>
                        <span>&ldquo;{q}&rdquo;</span>
                      </div>
                      <div
                        className="grid transition-all duration-300 ease-in-out"
                        style={{
                          gridTemplateRows: isAsked ? '1fr' : '0fr',
                          opacity: isAsked ? 1 : 0,
                          marginTop: isAsked ? '0.25rem' : '0px',
                        }}
                      >
                        <div className="overflow-hidden">
                          <span className="text-[10px] font-semibold text-blue-500 pl-5 not-italic block pb-0.5">
                            Marked as asked
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Legacy life-stage-split: Childhood + Adulthood collapsible accordions.
              <div className="flex flex-col gap-1">
                <CollapsibleQuestionSection
                  title="Childhood"
                  questions={criterion.childhoodProbingQuestions || []}
                  isExpanded={expandedSections.has('childhood')}
                  onToggle={() => toggleSection('childhood')}
                  askedQuestions={askedQuestionsSet}
                  onToggleAsked={toggleAskedQuestion}
                />
                <CollapsibleQuestionSection
                  title="Adulthood"
                  questions={criterion.adulthoodProbingQuestions || []}
                  isExpanded={expandedSections.has('adulthood')}
                  onToggle={() => toggleSection('adulthood')}
                  askedQuestions={askedQuestionsSet}
                  onToggleAsked={toggleAskedQuestion}
                />
              </div>
            )}
          </div>

          {/* Column 2 — Childhood notes (content height; grows with notes) */}
          <div className="flex-1 min-w-0 flex flex-col bg-gray-50 rounded-2xl border border-gray-200 p-4">
            <h2 className="text-slate-800 text-lg font-bold leading-tight text-left mb-3">
              Childhood
            </h2>

            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Threshold met:<span className="text-red-500 ml-0.5">*</span></h3>
            <div className="mb-3">
              {renderEvidenceSelector(childhoodEvidence, 'evidence_childhood', 'Evidence for Childhood')}
            </div>

            {showDomains && (
              <>
                <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Domains Impacted:</h3>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {CHILDHOOD_DOMAINS.map(domain => {
                    const selected = (val('domains_childhood') || []);
                    const isSelected = selected.includes(domain.id);
                    const domainLabel = `Domain Impacted: ${domain.title} `;
                    return (
                      <button
                        key={domain.id}
                        type="button"
                        draggable={!readOnly}
                        onDragStart={readOnly ? undefined : (e) => {
                          e.dataTransfer.setData(DOMAIN_MIME, domainLabel);
                          e.dataTransfer.setData('text/plain', domainLabel);
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onClick={readOnly ? undefined : () => {
                          const next = isSelected
                            ? selected.filter(d => d !== domain.id)
                            : [...selected, domain.id];
                          onFieldChange('domains_childhood', next);
                        }}
                        disabled={readOnly}
                        className={`
                          px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150
                          ${readOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
                          ${isSelected
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : 'hover:border-primary/40 hover:text-primary'}`}
                        `}
                        aria-pressed={isSelected}
                        title={readOnly ? undefined : "Click to select/deselect \u2014 drag into notes to create a domain note"}
                      >
                        {domain.title}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <StructuredNotesEditor
              key={`${criterion.id}_childhood`}
              notes={val('notes_childhood') || []}
              onChange={readOnly ? undefined : (v) => onFieldChange('notes_childhood', v)}
              placeholder="Type a note and press Enter..."
              readOnlyPlaceholder="No childhood notes recorded"
              readOnly={readOnly}
              minHeight="15rem"
              domainId="notes_childhood"
              onMoveNoteIn={readOnly ? undefined : handleMoveNote}
              moveTargets={moveTargets}
              currentMoveId={`${criterion.id}:notes_childhood`}
              onMoveNoteTo={readOnly ? undefined : onMoveNote}
              onProbingQuestionDropped={readOnly ? undefined : markQuestionAsAsked}
            />
          </div>

          {/* Column 3 — Adulthood notes (content height; grows with notes) */}
          <div className="flex-1 min-w-0 flex flex-col bg-gray-50 rounded-2xl border border-gray-200 p-4">
            <h2 className="text-slate-800 text-lg font-bold leading-tight text-left mb-3">
              Adulthood
            </h2>

            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Threshold met:<span className="text-red-500 ml-0.5">*</span></h3>
            <div className="mb-3">
              {renderEvidenceSelector(adulthoodEvidence, 'evidence_adulthood', 'Evidence for Adulthood')}
            </div>

            {showDomains && (
              <>
                <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Domains Impacted:</h3>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => {
                    const selected = (val('domains_adulthood') || []);
                    const isSelected = selected.includes(domain.id);
                    const domainLabel = `Domain Impacted: ${domain.title} `;
                    return (
                      <button
                        key={domain.id}
                        type="button"
                        draggable={!readOnly}
                        onDragStart={readOnly ? undefined : (e) => {
                          e.dataTransfer.setData(DOMAIN_MIME, domainLabel);
                          e.dataTransfer.setData('text/plain', domainLabel);
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onClick={readOnly ? undefined : () => {
                          const next = isSelected
                            ? selected.filter(d => d !== domain.id)
                            : [...selected, domain.id];
                          onFieldChange('domains_adulthood', next);
                        }}
                        disabled={readOnly}
                        className={`
                          px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150
                          ${readOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
                          ${isSelected
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : 'hover:border-primary/40 hover:text-primary'}`}
                        `}
                        aria-pressed={isSelected}
                        title={readOnly ? undefined : "Click to select/deselect \u2014 drag into notes to create a domain note"}
                      >
                        {domain.title}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <StructuredNotesEditor
              key={`${criterion.id}_adulthood`}
              notes={val('notes_adulthood') || []}
              onChange={readOnly ? undefined : (v) => onFieldChange('notes_adulthood', v)}
              placeholder="Type a note and press Enter..."
              readOnlyPlaceholder="No adulthood notes recorded"
              readOnly={readOnly}
              minHeight="15rem"
              domainId="notes_adulthood"
              onMoveNoteIn={readOnly ? undefined : handleMoveNote}
              moveTargets={moveTargets}
              currentMoveId={`${criterion.id}:notes_adulthood`}
              onMoveNoteTo={readOnly ? undefined : onMoveNote}
              onProbingQuestionDropped={readOnly ? undefined : markQuestionAsAsked}
            />
          </div>
        </div>
      </div>

      {/* Bottom navigation — within-phase criterion stepping as primary CTAs;
          cross-phase History / Review jumps as skip links (ADHD SSI pattern).
          Go to Review (button or link) routes through onNavigate('REVIEW') so
          incomplete-evidence validation matches the sidebar Review click. */}
      <ModularStepFooter
        showBack={!!prevCriterion}
        backLabel={prevCriterion?.id}
        onBack={prevCriterion ? () => onNavigate(prevCriterion.id) : undefined}
        onModuleExit={!prevCriterion && onBackToHistory ? onBackToHistory : undefined}
        moduleExitLabel={!prevCriterion && onBackToHistory ? 'Back to History' : undefined}
        phaseBackLink={
          prevCriterion && onBackToHistory
            ? { label: 'Back to History', onClick: onBackToHistory }
            : undefined
        }
        rightActions={(
          <button
            type="button"
            onClick={() => onNavigate(nextCriterion ? nextCriterion.id : 'REVIEW')}
            className="flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-white bg-primary hover:bg-primary/90"
          >
            <span className="font-semibold text-sm">
              {nextCriterion ? `Next: ${nextCriterion.id}` : 'Go to Review'}
            </span>
            <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
          </button>
        )}
        phaseForwardLink={
          nextCriterion
            ? { label: 'Go to Review', onClick: () => onNavigate('REVIEW') }
            : undefined
        }
      />

      {/* Asked-questions popup overlay */}
      {showAskedPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowAskedPopup(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setShowAskedPopup(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            <h2 className="text-base font-bold text-gray-900 mb-0.5">
              {criterion.id}. {criterion.shortTitle}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Probing Questions Asked ({askedQuestionsArray.length})
            </p>

            <div className="flex flex-col gap-1.5">
              {askedQuestionsArray.map((q, idx) => (
                <div
                  key={idx}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                    bg-blue-50 text-blue-700 border border-blue-200"
                >
                  &ldquo;{q}&rdquo;
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* SEQ details popup — shown for B4 only when the patient's org assigned
          the Sensory Experiences Questionnaire and the clinician clicks
          "View Sensory Experiences Questionnaire". FormDetailsPanel is the same component used by
          PatientHistoryPanel on the Patient Overview page. */}
      {criterion.id === 'B4' && seqFormData && (
        <FormDetailsPanel
          isOpen={isSeqPanelOpen}
          formInfo={{ key: 'sensoryExperiences', source: 'patient' }}
          patientData={{ formData: { sensoryExperiences: seqFormData } }}
          onClose={() => setIsSeqPanelOpen(false)}
          displayMode="full"
        />
      )}
    </div>
  );
}
